/**
 * Framework-agnostic token storage shared by the HTTP client and the auth hooks.
 *
 * Backend contract (IdentityService): access tokens live **15 minutes**
 * (`accessExpiresAt` comes back on every login/refresh — never assume a fixed
 * lifetime) and refresh tokens **rotate on every use** (the old one is revoked
 * server-side the moment it's redeemed). SSR-safe: falls back to memory when
 * `window` is unavailable.
 */

export interface TokenBundle {
  accessToken: string;
  /** ISO timestamp from the backend's `accessExpiresAt`. */
  accessExpiresAt: string;
  refreshToken: string;
}

const STORAGE_KEY = 'sv-tokens';
/** Refresh this many ms before actual expiry so in-flight requests never race it. */
const REFRESH_SKEW_MS = 60_000;

let memory: TokenBundle | null = null;
type Listener = (tokens: TokenBundle | null) => void;
const listeners = new Set<Listener>();

function persist(tokens: TokenBundle | null) {
  memory = tokens;
  if (typeof window !== 'undefined') {
    try {
      if (tokens) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage unavailable (private mode etc.) — memory fallback still works */
    }
  }
  listeners.forEach((l) => l(tokens));
}

export const tokenStore = {
  get(): TokenBundle | null {
    if (memory) return memory;
    if (typeof window !== 'undefined') {
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw) memory = JSON.parse(raw) as TokenBundle;
      } catch {
        /* corrupted entry — treat as signed out */
      }
    }
    return memory;
  },

  set(tokens: TokenBundle) {
    persist(tokens);
  },

  clear() {
    persist(null);
  },

  /** True when an access token exists and is not inside the refresh window. */
  isAccessTokenFresh(): boolean {
    const tokens = tokenStore.get();
    if (!tokens) return false;
    return Date.parse(tokens.accessExpiresAt) - REFRESH_SKEW_MS > Date.now();
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
